import { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import type { AdminGetRoutesResDto } from "service/api/estabrookSchemas";
import { RouteStatus } from "../pages/dashboard/route-planner/helpers/route-status.enum";
import { getMinutesOfDay } from "../utils/date-time";

interface UseRouteStartTimeValidationProps {
  routes: AdminGetRoutesResDto[];
  currentRouteStatus?: RouteStatus;
}

interface ValidationResult {
  hasInvalidStartTime: boolean;
  invalidRoutes: AdminGetRoutesResDto[];
}

export const useRouteStartTimeValidation = ({
  routes,
  currentRouteStatus,
}: UseRouteStartTimeValidationProps): ValidationResult => {
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    hasInvalidStartTime: false,
    invalidRoutes: [],
  });

  const validateStartTimes = useCallback(() => {
    // In planning phase, allow all start times - no validation needed
    if (currentRouteStatus === RouteStatus.Planning) {
      setValidationResult({
        hasInvalidStartTime: false,
        invalidRoutes: [],
      });
      return;
    }

    const now = DateTime.local();
    const currentMinutesOfDay = getMinutesOfDay(now);
    const invalidRoutes: AdminGetRoutesResDto[] = [];

    // For planned and live phases, routes with past start times prevent dispatch
    // This validation is for dispatch/create routes functionality
    // Individual editing restrictions are handled in the UI components
    routes.forEach((route) => {
      // Skip unassigned route (virtual route for UI purposes)
      if (route.id === "unassigned-route" || !route.startTime) {
        return;
      }

      // If the route date is in the future, allow dispatch regardless of start time
      if (route.date) {
        const routeDate = DateTime.fromISO(route.date);
        if (routeDate.isValid) {
          // Compare dates by converting to ISO date strings (YYYY-MM-DD)
          // This ensures correct comparison across months and years
          const routeDateOnly = routeDate.toISODate();
          const todayDateOnly = now.toISODate();

          if (routeDateOnly && todayDateOnly && routeDateOnly > todayDateOnly) {
            return; // Skip validation for future dates - allow dispatch
          }
        }
      }

      // Parse the route start time to get the time of day
      const routeTimeOnly = DateTime.fromFormat(route.startTime, "HH:mm");

      if (!routeTimeOnly.isValid) {
        return; // Skip routes with invalid time format
      }

      // Check if the route start time is the current time or earlier
      // This ignores date entirely - only compares the time of day
      if (getMinutesOfDay(routeTimeOnly) <= currentMinutesOfDay) {
        invalidRoutes.push(route);
      }
    });

    const hasInvalidStartTime = invalidRoutes.length > 0;

    setValidationResult({
      hasInvalidStartTime,
      invalidRoutes,
    });
  }, [routes, currentRouteStatus]);

  // Initial validation
  useEffect(() => {
    validateStartTimes();
  }, [validateStartTimes]);

  // Set up real-time validation - check every minute
  useEffect(() => {
    const interval = setInterval(() => {
      validateStartTimes();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [validateStartTimes]);

  return validationResult;
};
